import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

const InquiryModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError('Name, email, and message are required.');
      return;
    }
    setError('');
    setStatus('loading');
    try {
      await axios.post(`${API_BASE_URL}/leads`, form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
      setStatus('idle');
    }
  };

  const resetAndClose = () => {
    setStatus('idle');
    setError('');
    setForm({ name: '', email: '', phone: '', message: '' });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={resetAndClose} />

          <motion.div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}>
            <button onClick={resetAndClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10">
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <div className="h-1.5 bg-gradient-to-r from-pink-500 to-purple-600 rounded-t-3xl" />

            <div className="p-6">
              {status === 'success' ? (
                <motion.div className="text-center py-6" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">Thanks — we’ll contact you</h3>
                  <p className="text-gray-600 mb-4">Our team will reach out soon.</p>
                  <button onClick={resetAndClose} className="primary-btn px-6 py-2 rounded-full text-white font-semibold">Close</button>
                </motion.div>
              ) : (
                <>
                  <h3 className="font-heading text-xl font-bold text-gray-900 mb-1">Quick Inquiry</h3>
                  <p className="text-gray-500 text-sm mb-4">Share your details and we’ll get back to you.</p>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} placeholder="Your name" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Email *</label>
                      <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@company.com" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Phone</label>
                      <input name="phone" value={form.phone} onChange={handleChange} placeholder="+91 90091 11156" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Message</label>
                      <textarea name="message" value={form.message} onChange={handleChange} rows={3} required placeholder="Tell us briefly..." className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>

                    {error && <p className="text-red-500 text-sm">{error}</p>}

                    <button type="submit" disabled={status === 'loading'} className="w-full primary-btn py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-70">
                      {status === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Send Inquiry</>}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InquiryModal;
